import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { streamAsset } from "@/api/mist";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

const shuffled = (n) => {
  const idx = [...Array(n).keys()];
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
};

/* A single global <audio> that keeps playing across every view / route. */
export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const duckRef = useRef(false);

  const [queue, setQueue] = useState([]); // [{id,title,coverId,genre}]
  const [order, setOrder] = useState([]); // indices into queue
  const [pos, setPos] = useState(0); // position within order
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [progress, setProgress] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const current = queue[order[pos]] || null;

  const loadAndPlay = useCallback(async (item) => {
    const a = audioRef.current;
    if (!a || !item) return;
    try {
      let src;
      if (item.audioUrl) {
        if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
        src = item.audioUrl;
      } else {
        const url = await streamAsset(item.id);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        src = url;
      }
      a.src = src;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } catch {
      setPlaying(false);
    }
  }, []);

  // Load whenever the active position changes.
  useEffect(() => {
    if (current) loadAndPlay(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, pos]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => setPos((p) => (order.length ? (p + 1) % order.length : 0));
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [order.length]);

  const playQueue = useCallback((items, startIndex = 0) => {
    if (!items || !items.length) return;
    setQueue(items);
    if (shuffle) {
      const ord = shuffled(items.length);
      const at = ord.indexOf(startIndex);
      if (at > 0) { ord.splice(at, 1); ord.unshift(startIndex); }
      setOrder(ord);
      setPos(0);
    } else {
      setOrder([...Array(items.length).keys()]);
      setPos(Math.max(0, Math.min(startIndex, items.length - 1)));
    }
    setExpanded(false);
  }, [shuffle]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); } else { a.pause(); setPlaying(false); }
  }, [current]);

  const next = useCallback(() => setPos((p) => (order.length ? (p + 1) % order.length : 0)), [order.length]);
  const prev = useCallback(() => setPos((p) => (order.length ? (p - 1 + order.length) % order.length : 0)), [order.length]);

  const toggleShuffle = useCallback(() => {
    setShuffle((s) => {
      const ns = !s;
      setOrder((ord) => {
        const cur = ord[pos];
        const no = ns ? shuffled(queue.length) : [...Array(queue.length).keys()];
        if (ns && cur != null) {
          const at = no.indexOf(cur);
          if (at > 0) { no.splice(at, 1); no.unshift(cur); }
          setPos(0);
        } else if (!ns && cur != null) {
          setPos(no.indexOf(cur));
        }
        return no;
      });
      return ns;
    });
  }, [pos, queue.length]);

  const seek = useCallback((frac) => {
    const a = audioRef.current;
    if (a && a.duration) a.currentTime = frac * a.duration;
  }, []);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute("src"); a.load(); }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setQueue([]); setOrder([]); setPos(0); setPlaying(false); setExpanded(false);
  }, []);

  // Ducking so the video "TV" can take over audio, then resume music on close.
  const duck = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    duckRef.current = !a.paused && !!current;
    a.pause();
    setPlaying(false);
  }, [current]);
  const unduck = useCallback(() => {
    const a = audioRef.current;
    if (!a || !duckRef.current) return;
    a.play().then(() => setPlaying(true)).catch(() => {});
    duckRef.current = false;
  }, []);

  return (
    <PlayerCtx.Provider value={{
      current, playing, shuffle, progress, expanded, setExpanded,
      queueLength: order.length, position: pos,
      playQueue, toggle, next, prev, toggleShuffle, seek, close, duck, unduck,
      hasQueue: order.length > 0,
    }}>
      <audio ref={audioRef} className="hidden" data-testid="global-audio" preload="auto" />
      {children}
    </PlayerCtx.Provider>
  );
}
